import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import * as _ from 'lodash';

import { getGPSData, sendGPSEmail } from '../services/gps/api';
import { GetGPSDataPayload } from '../services/gps/types';
import type { AppDispatch } from '../store';
import { RootState } from '../store';
import { initialState } from './interfaces';
import { GPSDataResponse, GPSRangeDataResponse } from '../gps/interfaces';
import { createResetAction } from '../../utils';
import { SendGPSEmailPayloadType } from './../services/gps/types';

let currentAbortController: AbortController | null = null;
type GPSPayload = GPSDataResponse | GPSRangeDataResponse | null;
type FetchGPSDataPayload = Omit<GetGPSDataPayload, 'filters'> & {
  filters?: GetGPSDataPayload['filters'] & {
    startDate?: string;
    endDate?: string;
  };
};

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : 'Unknown error';

const isGPSRangeDataResponse = (
  payload: GPSPayload
): payload is GPSRangeDataResponse =>
  !!payload && 'days' in payload && Array.isArray(payload.days);

const getGPSMessage = (payload: GPSPayload): string | null =>
  payload && 'msg' in payload ? payload.msg ?? null : null;

// Async thunk for fetching GPS data
export const fetchGPSData = createAsyncThunk<
  GPSPayload,
  FetchGPSDataPayload,
  { dispatch: AppDispatch; rejectValue: string }
>(
  'gps/fetchGPSData',
  async (
    payload: FetchGPSDataPayload,
    { dispatch, rejectWithValue, signal },
  ) => {
    try {
      // Stale safety: abort previous request on new Apply.
      if (currentAbortController) currentAbortController.abort();
      const ac = new AbortController();
      currentAbortController = ac;

      if (signal) {
        if (signal.aborted) {
          ac.abort();
        } else {
          signal.addEventListener('abort', () => ac.abort(), { once: true });
        }
      }

      const response = await getGPSData(payload, {
        dispatch,
        signal: ac.signal,
      });
      const statusCode = _.get(response, 'statusCode', null);
      if (statusCode === 200) {
        return response?.data || null;
      }
      return null;
    } catch (err: unknown) {
      return rejectWithValue(getErrorMessage(err));
    }
  }
);

export const sendGPSBriefyingEmail = createAsyncThunk<
  void,
  SendGPSEmailPayloadType,
  { dispatch: AppDispatch; rejectValue: string }
>(
  'gps/sendGPSData',
  async (payload: SendGPSEmailPayloadType, { dispatch, rejectWithValue }) => {
    try {
      await sendGPSEmail(payload, { dispatch });
    } catch (err: unknown) {
      return rejectWithValue(getErrorMessage(err));
    }
  }
);

// GPS slice
const gpsSlice = createSlice({
  name: 'gps',
  initialState,
  reducers: {
    resetGPS: createResetAction(initialState),
    clearGPSError: (state) => {
      state.error = null;
    },
    setSelectedDate: (state, action: PayloadAction<string>) => {
      state.selectedDate = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchGPSData.pending, (state, action) => {
      state.loading = true;
      state.error = null;
      state.data = null;
      state.rangeData = null;
      state.activeRequestId = action.meta.requestId;
      const filters = action.meta.arg?.filters;
      const appliedStart =
        filters?.date ?? filters?.startDate;
      const appliedEnd =
        filters?.date ?? filters?.endDate;
      if (appliedStart && appliedEnd) {
        state.appliedStartDate = appliedStart;
        state.appliedEndDate = appliedEnd;
      }
    });
    builder.addCase(
      fetchGPSData.fulfilled,
      (state, action) => {
        if (action.meta.requestId !== state.activeRequestId) return;
        state.loading = false;
        const payload = action.payload;

        if (isGPSRangeDataResponse(payload)) {
          state.rangeData = payload;
          state.data =
            payload.days?.find(
              (d) =>
                Array.isArray(d?.operatories) && d.operatories.length > 0,
            ) ??
            payload.days?.[0] ??
            null;
          state.appliedStartDate = payload.startDate;
          state.appliedEndDate = payload.endDate;
          state.selectedDate = payload.endDate;
        } else {
          state.rangeData = null;
          state.data = payload;
          const dayDate =
            payload?.metadata?.date ??
            state.appliedStartDate ??
            state.selectedDate;
          if (dayDate) {
            state.appliedStartDate = dayDate;
            state.appliedEndDate = dayDate;
            state.selectedDate = dayDate;
          }
        }

        state.error = null;
        state.lastFetched = new Date().toISOString();
        state.msg = getGPSMessage(payload);
        state.activeRequestId = null;
      }
    );
    builder.addCase(fetchGPSData.rejected, (state, action) => {
      if (action.meta.requestId !== state.activeRequestId) return;
      state.loading = false;
      state.error = action.payload ?? action.error.message ?? 'Failed to fetch GPS data';
      state.activeRequestId = null;
    });
    builder.addCase(sendGPSBriefyingEmail.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(sendGPSBriefyingEmail.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
    });
    builder.addCase(sendGPSBriefyingEmail.rejected, (state) => {
      state.loading = false;
    });
  }
});

export const { resetGPS, clearGPSError, setSelectedDate } = gpsSlice.actions;
export default gpsSlice.reducer;

// Basic selectors
export const selectGPSData = (state: RootState) => state.gps.data;
export const selectGPSLoading = (state: RootState) => state.gps.loading;
export const selectGPSError = (state: RootState) => state.gps.error;
export const selectGPSLastFetched = (state: RootState) => state.gps.lastFetched;
export const getSelectedDate = (state: RootState) => state.gps?.selectedDate;

// Derived selectors
export const selectGPSOperatories = (state: RootState) =>
  state.gps.data?.operatories || [];
export const selectGPSMetadata = (state: RootState) => state.gps.data?.metadata;
export const selectGPSTotalPatients = (state: RootState) =>
  state.gps.data?.metadata?.totalPatients || 0;
export const selectGPSTotalAppointments = (state: RootState) =>
  state.gps.data?.metadata?.totalAppointments || 0;
export const selectGPSTotalItems = (state: RootState) =>
  state.gps.data?.metadata?.totalItems || 0;
export const selectGPSTotalOperatories = (state: RootState) =>
  state.gps.data?.metadata?.totalOperatories || 0;
export const selectGPSProviders = (state: RootState) =>
  state.gps.data?.providers || [];

// Check if GPS data is available
export const selectGPSDataAvailable = (state: RootState) => !!state.gps.data;
export const selectGPSRangeData = (state: RootState) => state.gps.rangeData;
export const selectGPSAppliedStartDate = (state: RootState) =>
  state.gps.appliedStartDate;
export const selectGPSAppliedEndDate = (state: RootState) =>
  state.gps.appliedEndDate;
export const selectDailyBriefingData = (state: RootState) => {
  const briefingData = state.gps.data?.dailyBriefing;
  const narrativeData = state.gps.data?.narrative;
  const metadata = state.gps.data?.metadata;

  return {
    date: briefingData?.date || new Date().toISOString().split('T')[0],
    streak: briefingData?.streak || 0,
    teaser: briefingData?.teaser,
    narrative: narrativeData || {
      executive_summary: 'Not available',
      critical_priorities: [],
      production_intelligence: [],
      operational_excellence: [],
      strategic_recommendations: [],
      closing_directive: ''
    },
    insights: briefingData?.insights || [],
    achievements: briefingData?.achievements || [],
    totalPatients: metadata?.totalPatients || 0,
    keyMetrics: briefingData?.keyMetrics || [],
    isLoading: state.gps?.loading
  };
};

export const selecteGPSAPIMsg = (state: RootState) => state?.gps?.msg;
